import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Input,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, Columns3, X } from 'lucide-react';
import * as React from 'react';

import { CondensedTip, useCondensed } from '@/design';
import type { IListField, IListFieldCatalogue } from '../../api/lists-contracts';
import { NAME_FIELD } from '../../list-criteria';
import { catalogueSections } from '../../lists';
import {
  FieldGroupHeading,
  FieldName,
  FieldSourcesHelp,
  HiddenFieldsNote,
  ProvenanceLine,
  matchesField,
  useFieldFolds
} from './field-picker';

/**
 * The columns — Conga's gear (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3.5; SR-221's grid note §5.1):
 * a search box, the view's columns in their order with ↑, ↓ and ✕, then the catalogue in its
 * groups (§2's words — Record, Profile, Survey questions, Staff fields…), a box per field, the
 * question number a muted prefix and never the heading (SR-221's R2). **The groups fold, and start
 * folded**, each heading counting the fields it offers; a search opens every group with a match and
 * leaves out the rest (`field-picker.tsx`, §3.13 — SR-403).
 *
 * **Columns belong to the view** (LST-03/04), never to local storage — the design system's column
 * picker keeps a viewer's choice there, which is the wrong owner here — so a change goes to the
 * caller, which marks the view changed. Name is pinned first while the catalogue offers it: a row
 * without it names nobody. A column the catalogue no longer offers (a field retired, or one the
 * caller may not see — survey-api leaves it out of the cells) stays listed as spelled, removable,
 * never dropped unasked.
 *
 * Lists draws it in a sheet (`ColumnsSheet`); Reports' Definition draws the editor inline
 * (`ColumnsEditor`), beside its Fields panel.
 */
/** The editor's parts, for a caller that places them apart — a report's builder draws the fields
 *  on the left and the columns in its main column (§4.2). */
export interface IColumnsEditorParts {
  /** The search box and the catalogue's groups, each field a box that adds it. */
  fields: React.ReactNode;
  /** The columns shown, in order, each with ↑ / ↓ and ✕. */
  shown: React.ReactNode;
}

/** A column a work page draws of its own — a field under its own label, or one it derives — offered
 *  beside the catalogue's, and shown again from its own group once removed. */
export interface IOwnColumn {
  id: string;
  label: string;
}

export function ColumnsEditor({
  catalogue,
  columns,
  onChange,
  split,
  own = NO_OWN,
  pinned,
  listsNote = true,
  iconOf,
  full = null
}: {
  catalogue: Nullable<IListFieldCatalogue>;
  columns: readonly string[];
  onChange: (columns: string[]) => void;
  /** Places the two parts apart; the search then reads the fields alone, the columns all drawn. */
  split?: (parts: IColumnsEditorParts) => React.ReactNode;
  /** A work page's own columns; Lists has none. */
  own?: readonly IOwnColumn[];
  /** The column held first — Name where the catalogue offers it, by default. */
  pinned?: Nullable<string>;
  /** Says where formatted text is edited — off where the catalogue is not Lists'. */
  listsNote?: boolean;
  /** A column's type as a small icon before its name — the SEC ADV register's; none elsewhere. */
  iconOf?: (code: string) => Nullable<ColumnIcon>;
  /** Why no more of the catalogue's fields can be added — a cap the caller has reached — said on
   *  hover over their boxes, then off; null while there is room. */
  full?: Nullable<string>;
}) {
  const [search, setSearch] = React.useState('');
  // A box moves between the lists as it is ticked, and the one pressed is unmounted with its
  // focus: the column's new control takes it, or the keys after it would land on nothing.
  const [moved, setMoved] = React.useState<Nullable<string>>(null);
  const body = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!moved) return;
    body.current
      ?.querySelector<HTMLElement>(`[data-sheet-column="${CSS.escape(moved)}"] button`)
      ?.focus();
    setMoved(null);
  }, [moved]);

  const sections = React.useMemo(
    () => catalogueSections(catalogue, undefined, new Set(columns)),
    [catalogue, columns]
  );
  const fields = React.useMemo(() => {
    const held = new Map<string, IListField>();
    for (const section of sections) for (const field of section.fields) held.set(field.code, field);
    return held;
  }, [sections]);
  const first = pinned === undefined ? (fields.has(NAME_FIELD) ? NAME_FIELD : null) : pinned;
  const shown = first ? [first, ...columns.filter((code) => code !== first)] : [...columns];
  const ownLabel = (code: string) => own.find((column) => column.id === code)?.label;
  const iconFor = (code: string, className: string) => {
    const Icon = iconOf?.(code);
    if (!Icon) return null;
    return <Icon className={cn('size-3.5 shrink-0 text-muted-foreground', className)} aria-hidden />;
  };

  const folds = useFieldFolds(search);
  const idBase = React.useId();
  const hitShown = (code: string) => split !== undefined || hit(code);
  const hit = (code: string) => {
    if (!folds.searching) return true;
    const field = fields.get(code);
    if (!field) return (ownLabel(code) ?? code).toLowerCase().includes(search.trim().toLowerCase());
    return matchesField(field, search);
  };

  const move = (index: number, by: -1 | 1) => {
    const next = [...shown];
    const [code] = next.splice(index, 1);
    next.splice(index + by, 0, code);
    onChange(next);
  };
  const top = first ? 1 : 0;

  const searchBox = (
    <div className="grid">
      <Input
        type="search"
        aria-label={split ? 'Search the fields' : 'Search the columns'}
        placeholder="Search fields…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="h-8"
      />
      <HiddenFieldsNote sections={sections} />
    </div>
  );
  const shownPart = (
      <section aria-label="Shown, in order" className="grid grid-cols-[minmax(0,1fr)] gap-1.5">
        <h3 className="text-xs font-medium text-[var(--veritant-royal-70)]">Shown, in order</h3>
        {shown.length === 0 && <p className="text-muted-foreground">No column yet.</p>}
        {/* One track the section's width: an auto one took the longest question's width on one
            line, and every row's ↑ ↓ ✕ went past the sheet's right edge. */}
        <ol className="grid grid-cols-[minmax(0,1fr)] gap-1">
          {shown.map((code, index) => {
            if (!hitShown(code)) return null;
            const field = fields.get(code);
            const label = ownLabel(code) ?? field?.label ?? code;
            const isPinned = first !== null && index === 0;
            return (
              <li key={code} data-sheet-column={code} className="flex min-h-7 items-start gap-2">
                {iconFor(code, 'mt-[0.4375rem]')}
                {field || !ownLabel(code) ? (
                  <ColumnName field={field} code={code} />
                ) : (
                  <span className="min-w-0 flex-1 break-words py-1">{label}</span>
                )}
                {isPinned ? (
                  <span className="ml-auto shrink-0 py-1 text-xs text-muted-foreground">Always first</span>
                ) : (
                  <span className="ml-auto flex shrink-0 gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      className="size-7 p-0"
                      aria-label={`Move ${label} up`}
                      disabled={index <= top}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUp className="size-3.5" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="size-7 p-0"
                      aria-label={`Move ${label} down`}
                      disabled={index === shown.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDown className="size-3.5" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="size-7 p-0"
                      aria-label={`Remove ${label}`}
                      onClick={() => {
                        onChange(shown.filter((one) => one !== code));
                        setMoved(code);
                      }}
                    >
                      <X className="size-3.5" aria-hidden />
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </section>
  );
  const offered = sections
    .map((section) => ({
      section,
      fields: section.fields.filter((field) => !shown.includes(field.code) && hit(field.code))
    }))
    // A search leaves out the groups it matches nothing in; without one, every group is drawn.
    .filter(({ fields }) => !folds.searching || fields.length > 0);
  const ownOffered = own.filter((column) => !shown.includes(column.id) && hit(column.id));
  const sectionsPart = (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-1">
      {ownOffered.length > 0 && (
        <section aria-label="On this page" className="grid grid-cols-[minmax(0,1fr)] gap-0.5">
          <h3 className="text-xs font-medium text-[var(--veritant-royal-70)]">On this page</h3>
          <ul className="grid grid-cols-[minmax(0,1fr)] gap-0.5">
            {ownOffered.map((column) => (
              <li key={column.id} data-sheet-column={column.id} className="flex min-h-7 items-start gap-2 py-1">
                <Checkbox
                  className="mt-px"
                  aria-label={`Show ${column.label}`}
                  checked={false}
                  onCheckedChange={() => {
                    onChange([...shown, column.id]);
                    setMoved(column.id);
                  }}
                />
                {iconFor(column.id, 'mt-0.5')}
                <span className="min-w-0 flex-1 break-words">{column.label}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {folds.searching && offered.length === 0 && ownOffered.length === 0 && (
        <p className="text-muted-foreground">No field matches the search.</p>
      )}
      {offered.map(({ section, fields }) => {
        const open = folds.isOpen(section.key);
        const id = `${idBase}-${section.key}`;
        return (
          <section key={section.key} aria-label={section.title} className="grid grid-cols-[minmax(0,1fr)] gap-0.5">
            <h3>
              <FieldGroupHeading
                section={section}
                count={fields.length}
                open={open}
                controls={id}
                onToggle={() => folds.toggle(section.key)}
              />
            </h3>
            {open && (
              <div id={id} className="pl-5">
                {fields.length === 0 ? (
                  <p className="text-muted-foreground">Every one is shown.</p>
                ) : (
                  <ul className="grid grid-cols-[minmax(0,1fr)] gap-0.5">
                    {fields.map((field) => (
                      <li
                        key={field.code}
                        data-sheet-column={field.code}
                        title={full ?? undefined}
                        className="flex min-h-7 items-start gap-2 py-1"
                      >
                        <Checkbox
                          className="mt-px"
                          aria-label={`Show ${field.label}`}
                          checked={false}
                          disabled={full !== null}
                          onCheckedChange={() => {
                            onChange([...shown, field.code]);
                            setMoved(field.code);
                          }}
                        />
                        {iconFor(field.code, 'mt-0.5')}
                        <span className="grid min-w-0 flex-1">
                          <FieldName field={field} wrap />
                          <ProvenanceLine field={field} survey={section.survey} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
  const note = listsNote && (
    <p className="text-xs text-muted-foreground">
      Formatted text is read and edited on the record page.
    </p>
  );

  if (split) {
    return (
      <div ref={body} className="contents text-[0.8125rem]">
        {split({
          fields: (
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4 text-[0.8125rem]">
              {searchBox}
              {sectionsPart}
              {note}
            </div>
          ),
          shown: <div className="text-[0.8125rem]">{shownPart}</div>
        })}
      </div>
    );
  }
  return (
    <div ref={body} className="grid gap-4 text-[0.8125rem]">
      {searchBox}
      {shownPart}
      {sectionsPart}
      {note}
    </div>
  );
}

const NO_OWN: readonly IOwnColumn[] = [];

/** A type icon, as the sheet draws one before a column's name. */
export type ColumnIcon = React.ComponentType<{ className?: string }>;

/** A column's name, its question number a muted prefix; one the catalogue lacks as spelled. Wrapped,
 *  never cut: two questions can differ only at their end ("… low end of range", "… high end"). */
function ColumnName({ field, code }: { field: IListField | undefined; code: string }) {
  return (
    <span className="min-w-0 flex-1 py-1">
      {field ? (
        <FieldName field={field} wrap />
      ) : (
        <span className="break-words">
          {code} <span className="text-muted-foreground">— no longer offered</span>
        </span>
      )}
    </span>
  );
}

/** The editor behind the toolbar's *Columns* button, in a side sheet — the button its icon alone,
 *  of the same name, in a work page's compact bar. */
export function ColumnsSheet({
  catalogue,
  columns,
  onChange,
  description = 'Kept with the view, and saved with it.',
  own,
  pinned,
  listsNote,
  iconOf,
  full,
  lockedBy,
  lockedReason,
  commit
}: {
  catalogue: Nullable<IListFieldCatalogue>;
  columns: readonly string[];
  onChange: (columns: string[]) => void;
  /** A draft kept in the sheet and saved by a press (SR-500) in place of `onChange` per change:
   *  each answers null once done, else what went wrong; `reset` null while nothing is saved. */
  commit?: {
    save: (columns: string[]) => Promise<Nullable<string>>;
    reset: Nullable<() => Promise<Nullable<string>>>;
  };
  /** The id of the sentence saying why the viewer cannot change the columns: the button is off. */
  lockedBy?: string;
  /** That sentence, said on hovering the button that is off. */
  lockedReason?: string;
  /** Empty, none is drawn. */
  description?: string;
  own?: readonly IOwnColumn[];
  pinned?: Nullable<string>;
  listsNote?: boolean;
  iconOf?: (code: string) => Nullable<ColumnIcon>;
  full?: Nullable<string>;
}) {
  const [open, setOpen] = React.useState(false);
  const { condensed } = useCondensed();
  // The draft, begun from the columns shown each time the sheet opens.
  const [draft, setDraft] = React.useState<Nullable<string[]>>(null);
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  const opening = (next: boolean) => {
    setOpen(next);
    setDraft(next && commit ? [...columns] : null);
    setRefused(null);
  };
  const changed = draft !== null && draft.join(' ') !== columns.join(' ');
  // The columns read again while the sheet is open — a save refused as stale — start the draft
  // over from them, so a second press never writes over a colleague's newer choice.
  const shownKey = columns.join(' ');
  const opened = React.useRef(open);
  opened.current = open;
  React.useEffect(() => {
    if (opened.current && commit) setDraft(shownKey === '' ? [] : shownKey.split(' '));
    // `commit` is the caller's, renewed every render; only a new set of columns restarts the draft.
  }, [shownKey]);
  const run = async (write: () => Promise<Nullable<string>>) => {
    setBusy(true);
    setRefused(null);
    const said = await write();
    setBusy(false);
    if (said === null) opening(false);
    else setRefused(said);
  };
  return (
    <Sheet open={open} onOpenChange={opening}>
      <CondensedTip
        label={lockedBy !== undefined && lockedReason ? lockedReason : 'Columns'}
        always={lockedBy !== undefined}
      >
        <Button
          type="button"
          variant="outline"
          className={condensed ? 'size-8 p-0' : 'h-8 gap-1.5 font-normal'}
          aria-label={condensed ? 'Columns' : undefined}
          disabled={lockedBy !== undefined}
          aria-describedby={lockedBy}
          onClick={() => opening(true)}
        >
          <Columns3 className="size-4" aria-hidden />
          {!condensed && 'Columns'}
        </Button>
      </CondensedTip>
      <SheetContent
        side="right"
        className="w-[min(32rem,calc(100vw-2rem))] gap-0 overflow-y-auto p-0"
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <div className="grid gap-1 border-b border-border px-5 py-4">
          <div className="flex items-center gap-1">
            <SheetTitle className="text-[0.9375rem] text-[var(--color-royal-blue)]">Columns</SheetTitle>
            <FieldSourcesHelp />
          </div>
          {/* The kit's description is a screen reader's; a shared layout's — whose it is, who changed
              it last — is said on the sheet as well. */}
          {description && (
            <SheetDescription className={cn('text-[0.8125rem]', commit && 'not-sr-only text-muted-foreground')}>
              {description}
            </SheetDescription>
          )}
        </div>
        <div className="grid gap-4 px-5 py-4">
          <ColumnsEditor
            catalogue={catalogue}
            columns={draft ?? columns}
            onChange={commit ? setDraft : onChange}
            own={own}
            pinned={pinned}
            listsNote={listsNote}
            iconOf={iconOf}
            full={full}
          />
          {refused && (
            <p role="alert" className="text-[0.8125rem]">
              {refused}
            </p>
          )}
          {commit ? (
            // The draft's presses stay in sight at the sheet's foot, however long the list.
            <div className="sticky bottom-0 -mx-5 -mb-4 flex flex-wrap items-center gap-2 border-t border-border bg-white px-5 py-3">
              <Button
                type="button"
                className="h-8"
                disabled={busy || !changed}
                onClick={() => void run(() => commit.save(draft ?? [...columns]))}
              >
                {busy ? 'Saving…' : 'Save for everyone'}
              </Button>
              {commit.reset && (
                <Button type="button" variant="outline" className="h-8" disabled={busy} onClick={() => void run(commit.reset!)}>
                  Reset to default
                </Button>
              )}
              <SheetClose asChild>
                <Button type="button" variant="ghost" className="h-8" disabled={busy}>
                  Cancel
                </Button>
              </SheetClose>
            </div>
          ) : (
            <SheetClose asChild>
              <Button type="button" variant="outline" className="h-8 w-fit">
                Done
              </Button>
            </SheetClose>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
